import React from 'react';

// Live BLE signal meter. Renders a colored bar for the current RSSI (dBm) positioned against the
// tenant's Strong / Medium / Min-to-bind thresholds, so an admin can walk a beacon toward / away
// from a tracker and calibrate the numbers in Beacon Management.
const FLOOR = -100; // 0% of the bar
const CEIL = -30;   // 100% of the bar (very close)
const pct = (dbm) => Math.max(0, Math.min(100, ((dbm - FLOOR) / (CEIL - FLOOR)) * 100));

const LEVEL_BAR = { strong: 'bg-emerald-500', medium: 'bg-amber-500', low: 'bg-rose-500' };
const LEVEL_TEXT = {
  strong: 'text-emerald-600 dark:text-emerald-400',
  medium: 'text-amber-600 dark:text-amber-400',
  low: 'text-rose-600 dark:text-rose-400',
};

export const RssiMeter = ({ rssi, lastSeenAt, thresholds, level, testid }) => {
  if (rssi === null || rssi === undefined) {
    return <span className="text-muted-foreground text-xs" data-testid={testid}>No signal</span>;
  }
  const th = thresholds || { strong: -65, medium: -82, bind_min: -90 };
  const lvl = level || (rssi >= th.strong ? 'strong' : rssi >= th.medium ? 'medium' : 'low');
  const belowBind = th.bind_min != null && rssi < th.bind_min;
  const ageSec = lastSeenAt ? Math.max(0, (Date.now() - new Date(lastSeenAt).getTime()) / 1000) : Infinity;
  const fresh = ageSec < 15;
  const stale = ageSec > 120;
  const fill = pct(rssi);
  const label = belowBind ? "Won't bind" : (lvl === 'strong' ? 'Strong' : lvl === 'medium' ? 'Medium' : 'Low');

  return (
    <div className={`w-[150px] ${stale ? 'opacity-50' : ''}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-0.5">
        <span className="font-mono text-[11px] tabular-nums flex items-center gap-1" data-testid={testid ? `${testid}-value` : undefined}>
          {fresh && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="live" />}
          {rssi} dBm
        </span>
        <span className={`text-[10px] font-medium ${belowBind ? 'text-rose-600 dark:text-rose-400' : LEVEL_TEXT[lvl]}`} data-testid={testid ? `${testid}-label` : undefined}>{label}</span>
      </div>
      <div className="relative h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden" title={`Strong ≥ ${th.strong} · Medium ≥ ${th.medium} · Min-to-bind ${th.bind_min} dBm`}>
        <div className={`h-full rounded-full transition-all duration-500 ${belowBind ? 'bg-rose-400' : LEVEL_BAR[lvl]}`} style={{ width: `${fill}%` }} />
        {['bind_min', 'medium', 'strong'].filter((k) => th[k] != null).map((k) => (
          <span
            key={k}
            className="absolute top-0 h-full w-px bg-slate-600/70 dark:bg-slate-200/60"
            style={{ left: `${pct(th[k])}%` }}
            title={`${k.replace('_', ' ')}: ${th[k]} dBm`}
          />
        ))}
      </div>
      {stale && <p className="text-[9px] text-muted-foreground mt-0.5" data-testid={testid ? `${testid}-stale` : undefined}>stale</p>}
    </div>
  );
};

export default RssiMeter;
